"use client";

import React, {useEffect, useRef, useState} from "react";
import {Container} from "@/shared/components/shared";
import Script from "next/script";
import {Button} from "@/shared/components/ui/button";

declare global {
    interface Window {
        ymaps: any;
    }
}

export const Address: React.FC = () => {
    const [mapState, setMapState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapInstanceRef = useRef<any>(null);

    const yandexKey =
        process.env.NEXT_PUBLIC_YANDEX_API_KEY ||
        '5b2a9a4d-b5ee-425a-9f9d-34dd0477bcc2';

    const initMap = () => {
        if (!window.ymaps || !mapContainerRef.current) {
            console.error('YMaps not loaded or container not found');
            return;
        }

        try {
            setMapState('loading');

            window.ymaps.ready(() => {
                try {
                    if (mapInstanceRef.current) {
                        mapInstanceRef.current.destroy();
                        mapInstanceRef.current = null;
                    }

                    const map = new window.ymaps.Map(mapContainerRef.current, {
                        center: [43.117964, 131.93111],
                        zoom: 16,
                        controls: ['zoomControl', 'fullscreenControl'],
                    });

                    map.behaviors.disable("scrollZoom");

                    const marker = new window.ymaps.Placemark(
                        [43.117964, 131.93111],
                        {
                            hintContent: 'АвтоГарантСити',
                            balloonContent: 'Россия, Владивосток, ул. Шилкинская, 32а',
                        },
                        {
                            preset: 'islands#redIcon',
                        }
                    );

                    map.geoObjects.add(marker);
                    mapInstanceRef.current = map;
                    setMapState('loaded');

                    console.log('Map initialized successfully');
                } catch (error) {
                    console.error('Error creating map:', error);
                    setMapState('error');
                }
            });
        } catch (error) {
            console.error('Error in initMap:', error);
            setMapState('error');
        }
    };

    const handleScriptLoad = () => {
        console.log('Yandex Maps script loaded');
        setMapState('loaded');
    };

    const handleScriptError = () => {
        console.error('Failed to load Yandex Maps script');
        setMapState('error');
    };

    useEffect(() => {
        if (window.ymaps && mapState === 'loaded') {
            initMap();
        }
    }, [mapState]);

    useEffect(() => {
        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.destroy();
                mapInstanceRef.current = null;
            }
        };
    }, []);

    return (
        <section className={'pb-11 lg:pb-0'} id={'map'}>
            <Script
                src={`https://api-maps.yandex.ru/2.1/?apikey=${yandexKey}&lang=ru_RU`}
                strategy="afterInteractive"
                onLoad={handleScriptLoad}
                onError={handleScriptError}
            />

            <Container className={'pt-15 lg:pt-29 pb-11 lg:pb-13'}>
                <h2 className={'text-custom-orange font-bold text-[30px] lg:text-[70px] leading-[1.2] uppercase'}>адрес</h2>
                <h3 className={'text-custom-black text-[24px] lg:text-[45px] leading-[1.2] uppercase mt-5 lg:mt-7'}>ул.
                    шилкинская 32а</h3>
            </Container>
            <div
                ref={mapContainerRef}
                className="w-[339px] mx-auto lg:w-full rounded-lg lg:rounded-[0px] overflow-hidden relative h-[446px] lg:h-[715px]"
            >
                <Button
                    onClick={() => {
                        const [lat, lon] = [43.117964, 131.931110];
                        const url = `https://yandex.ru/maps/?rtext=~${lat},${lon}&rtt=auto`;
                        window.open(url, '_blank');
                    }}
                    className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3 hover:opacity-90 z-20 bottom-[40px] py-3 lg:py-5 px-6 lg:px-32 min-w-[302px]"
                >
                    <span className="inline-block" aria-hidden>
                      <svg
                          width="15"
                          height="22"
                          viewBox="0 0 15 22"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                            d="M0.0475068 4.37611C-0.0284181 4.22426 -0.0121365 4.04534 0.0909014 3.91519L2.94325 0.173511C3.02459 0.0650574 3.15475 0 3.29031 0C3.42588 0 3.55603 0.0650574 3.64279 0.173511L6.49514 3.91519C6.59818 4.04534 6.60901 4.22972 6.53853 4.37611C6.46261 4.52796 6.3108 4.62013 6.14266 4.62013H5.4106V8.45402C3.55603 9.37046 2.05935 10.8943 1.17544 12.7651V4.62013H0.443376C0.27528 4.62013 0.123431 4.52251 0.0475068 4.37611Z"
                            fill="white"
                        />
                        <path
                            d="M14.1145 10.8565L10.3728 13.7088C10.2427 13.8119 10.0583 13.8281 9.91189 13.7522C9.76004 13.6763 9.66787 13.5245 9.66787 13.3564V12.6243H9.14185C7.0812 12.6243 5.41101 14.2999 5.41101 16.3551V20.5795C5.41125 20.6372 5.40005 20.6944 5.37807 20.7478C5.35608 20.8012 5.32373 20.8497 5.2829 20.8906C5.24207 20.9314 5.19355 20.9637 5.14015 20.9857C5.08675 21.0077 5.02953 21.0189 4.97178 21.0187H1.6151C1.55736 21.0189 1.50013 21.0077 1.44673 20.9857C1.39333 20.9637 1.34482 20.9314 1.30398 20.8906C1.26315 20.8497 1.2308 20.8012 1.20882 20.7478C1.18683 20.6944 1.17563 20.6372 1.17588 20.5795V16.6751C1.17588 12.1091 4.89044 8.39459 9.45638 8.39459H9.67329V7.66253C9.67329 7.49443 9.76546 7.34258 9.91731 7.26666C10.0692 7.19615 10.2481 7.20702 10.3782 7.31005L14.1199 10.1624C14.2284 10.2437 14.2935 10.3739 14.2935 10.5148C14.288 10.6504 14.2284 10.7751 14.1145 10.8565Z"
                            fill="white"
                        />
                      </svg>
                    </span>
                    <span className="text-[16px] lg:text-[18px] text-white font-button text-bold leading-[1.2]">
                      Проложить маршрут
                    </span>
                </Button>
                {(mapState === 'loading' || mapState === 'idle') && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <div className="text-custom-black">
                            <div
                                className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-orange mx-auto mb-2"></div>
                            <p>Загрузка карты...</p>
                        </div>
                    </div>
                )}
                {mapState === 'error' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <div className="text-center">
                            <p className="text-red-500 font-semibold mb-2">
                                Не удалось загрузить карту
                            </p>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800 transition-colors"
                            >
                                Обновить страницу
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};